import { useRef, useState } from 'react';
import createLogger from '../common/logger';
import { s8sColor } from '../common/themes';
import IconVoice from './IconVoice';

const logger = createLogger('voice');

interface VoiceInputProps {
	onChange: (text: string) => void;
	disabled?: boolean;
	style?: React.CSSProperties;
	replyLocale?: string;
}

const VoiceInput = ({ onChange, disabled, style, replyLocale }: VoiceInputProps) => {
	const [isVoiceActive, setIsVoiceActive] = useState(false);
	const recognitionRef = useRef<any>(null);
	const speechLocale = replyLocale || 'zh-TW';

	const handleVoiceClick = async () => {
		if (disabled) return;
		if (!isVoiceActive) {
			try {
				await navigator.mediaDevices.getUserMedia({ audio: true });
			} catch (err) {
				logger.error('microphone permission denied', err);
				return;
			}
			if (!('webkitSpeechRecognition' in window)) {
				return;
			}
			const SpeechRecognition = (window as any).webkitSpeechRecognition;
			const recognition = new SpeechRecognition();
			recognition.lang = speechLocale;
			recognition.continuous = true;
			recognition.interimResults = false;
			recognition.onresult = (event: any) => {
				const { results } = event;
				const { transcript } = results[results.length - 1][0];
				onChange(transcript);
			};
			recognition.onend = () => {
				// 若還在語音模式則自動重啟
				if (recognitionRef.current && isVoiceActive) {
					recognitionRef.current.start();
				} else {
					setIsVoiceActive(false);
				}
			};
			recognition.onerror = (event: any) => {
				logger.error('speech recognition error', { error: event.error, event });
				setIsVoiceActive(false);
			};
			recognitionRef.current = recognition;
			recognition.start();
			setIsVoiceActive(true);
		} else {
			if (recognitionRef.current) {
				recognitionRef.current.onend = null; // 避免 stop 時自動重啟
				recognitionRef.current.stop();
			}
			setIsVoiceActive(false);
		}
	};

	return (
		<div style={style} onClick={handleVoiceClick}>
			<IconVoice iconColor={isVoiceActive ? s8sColor.brand.hover : s8sColor.neutral.gray800} disabled={disabled} />
		</div>
	);
};

VoiceInput.defaultProps = {
	disabled: false,
	style: undefined,
	replyLocale: undefined,
};

export default VoiceInput;
